import { create } from 'zustand';
import type {
  ReportDefinition,
  RowDefinition,
  ColumnDefinition,
} from '@calculom/report-designer';
import type { RowNode, LevelColumnConfig, ExcelLayoutSettings } from '@calculom/report-designer';
import {
  DEFAULT_LAYOUT_SETTINGS,
  flattenTree,
  insertNode,
  removeNode,
  updateNodeData,
  updateNodeRowId as updateNodeRowIdUtil,
  moveNode as moveNodeUtil,
  indentNode as indentNodeUtil,
  outdentNode as outdentNodeUtil,
  collectRowIds,
  createDefaultLevelConfig,
  updateNodeColumnHeadersId as updateNodeColumnHeadersIdUtil,
  clearColumnHeadersBindings,
} from '@calculom/report-designer';

// ============================================================
// DEFAULT VALUES
// ============================================================

const DEFAULT_THEME = {
  colors: {
    headerBackground: '#1e3a5f',
    sectionBackground: '#f0f4f8',
    subtotalBackground: '#e2e8f0',
  },
  negativeFormat: 'parentheses' as const,
};

function createDefaultColumns(): ColumnDefinition[] {
  return [
    { id: crypto.randomUUID(), header: 'Account', type: 'label' },
    {
      id: crypto.randomUUID(),
      header: 'Current Period',
      type: 'current',
      format: { type: 'currency', decimals: 2, negativeFormat: 'parentheses' },
    },
  ];
}

function createEmptyDefinition(): ReportDefinition {
  const now = new Date();
  return {
    id: crypto.randomUUID(),
    name: 'Untitled Report',
    type: 'custom',
    version: 1,
    parameters: [
      { id: crypto.randomUUID(), name: 'Company', type: 'company', required: true },
      { id: crypto.randomUUID(), name: 'Period', type: 'period', required: true },
    ],
    columns: createDefaultColumns(),
    rows: [],
    theme: DEFAULT_THEME,
    metadata: {
      createdAt: now,
      updatedAt: now,
    },
  };
}

// ============================================================
// HELPER: Sync definition.rows from nodes
// ============================================================

function syncRows(definition: ReportDefinition, nodes: RowNode[]): ReportDefinition {
  return {
    ...definition,
    rows: flattenTree(nodes).map((n) => n.data),
    metadata: { ...definition.metadata, updatedAt: new Date() },
  };
}

// ============================================================
// STATE INTERFACE
// ============================================================

export interface DesignerState {
  definition: ReportDefinition;
  nodes: RowNode[];
  columnConfig: LevelColumnConfig;
  layoutSettings: ExcelLayoutSettings;
  selectedRowId: string | null;
  selectedColumnId: string | null;
  columnModalOpen: boolean;
  isDirty: boolean;
  templateId: string | null;
  instructionText: string | null;

  // Report metadata
  setName: (name: string) => void;
  setType: (type: ReportDefinition['type']) => void;
  setTemplateId: (id: string | null) => void;

  // Legacy row actions (still work via tree operations)
  addRow: (row: RowDefinition, index?: number | undefined) => void;
  updateRow: (id: string, updates: Partial<RowDefinition>) => void;
  removeRow: (id: string) => void;
  reorderRows: (fromIndex: number, toIndex: number) => void;

  // Tree node actions
  addNode: (node: RowNode, afterId?: string | undefined, asChild?: boolean | undefined) => void;
  updateNode: (id: string, updates: Partial<RowDefinition>) => void;
  updateNodeRowId: (id: string, newRowId: string) => void;
  removeNode: (id: string) => void;
  moveNode: (fromId: string, toId: string) => void;
  indentNode: (id: string) => void;
  outdentNode: (id: string) => void;
  updateNodeColumnHeadersId: (id: string, columnHeadersId: string | undefined) => void;
  getFlatNodes: () => RowNode[];

  // Column actions
  addColumn: (column: ColumnDefinition) => void;
  updateColumn: (id: string, updates: Partial<ColumnDefinition>) => void;
  removeColumn: (id: string) => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  setColumns: (columns: ColumnDefinition[]) => void;

  // Level column config
  setLevelColumns: (depth: number, columns: ColumnDefinition[]) => void;
  setDefaultColumns: (columns: ColumnDefinition[]) => void;

  // Per-row column actions (for column_headers rows)
  addColumnToRow: (nodeId: string, column: ColumnDefinition) => void;
  updateColumnInRow: (nodeId: string, columnId: string, updates: Record<string, unknown>) => void;
  removeColumnFromRow: (nodeId: string, columnId: string) => void;
  reorderColumnsInRow: (nodeId: string, fromIndex: number, toIndex: number) => void;
  setColumnsForRow: (nodeId: string, columns: ColumnDefinition[]) => void;

  // Layout settings
  setLayoutSettings: (updates: Partial<ExcelLayoutSettings>) => void;

  // Column modal
  setColumnModalOpen: (open: boolean) => void;

  // Selection
  selectRow: (id: string | null) => void;
  selectColumn: (id: string | null) => void;

  // State management
  loadTemplate: (template: ReportDefinition, templateId?: string | undefined) => void;
  reset: () => void;
}

// ============================================================
// STORE (Zustand 4 pattern)
// ============================================================

export const useDesignerStore = create<DesignerState>()((set, get) => {
  const initialDef = createEmptyDefinition();
  const initialColumns = initialDef.columns;

  return {
    definition: initialDef,
    nodes: [],
    columnConfig: createDefaultLevelConfig(initialColumns),
    layoutSettings: { ...DEFAULT_LAYOUT_SETTINGS },
    selectedRowId: null,
    selectedColumnId: null,
    columnModalOpen: false,
    isDirty: false,
    templateId: null,
    instructionText: null,

    // ---- Report metadata ----

    setName: (name) =>
      set((state) => ({
        definition: {
          ...state.definition,
          name,
          metadata: { ...state.definition.metadata, updatedAt: new Date() },
        },
        isDirty: true,
      })),

    setType: (type) =>
      set((state) => ({
        definition: {
          ...state.definition,
          type,
          metadata: { ...state.definition.metadata, updatedAt: new Date() },
        },
        isDirty: true,
      })),

    setTemplateId: (id) => set({ templateId: id }),

    // ---- Legacy row actions (backward compat) ----

    addRow: (row, index) =>
      set((state) => {
        const rows = [...state.definition.rows];
        if (index !== undefined && index >= 0 && index <= rows.length) {
          rows.splice(index, 0, row);
        } else {
          rows.push(row);
        }
        return {
          definition: {
            ...state.definition,
            rows,
            metadata: { ...state.definition.metadata, updatedAt: new Date() },
          },
          isDirty: true,
        };
      }),

    updateRow: (id, updates) =>
      set((state) => {
        const newNodes = updateNodeData(state.nodes, id, updates);
        return {
          nodes: newNodes,
          definition: {
            ...state.definition,
            rows: state.definition.rows.map((row) =>
              row.id === id ? ({ ...row, ...updates } as RowDefinition) : row
            ),
            metadata: { ...state.definition.metadata, updatedAt: new Date() },
          },
          isDirty: true,
        };
      }),

    removeRow: (id) =>
      set((state) => {
        const newNodes = removeNode(state.nodes, id);
        return {
          nodes: newNodes,
          definition: syncRows(state.definition, newNodes),
          selectedRowId: state.selectedRowId === id ? null : state.selectedRowId,
          isDirty: true,
        };
      }),

    reorderRows: (fromIndex, toIndex) =>
      set((state) => {
        const rows = [...state.definition.rows];
        const [moved] = rows.splice(fromIndex, 1);
        if (moved) rows.splice(toIndex, 0, moved);
        return {
          definition: {
            ...state.definition,
            rows,
            metadata: { ...state.definition.metadata, updatedAt: new Date() },
          },
          isDirty: true,
        };
      }),

    // ---- Tree node actions ----

    addNode: (node, afterId, asChild) =>
      set((state) => {
        const newNodes = insertNode(state.nodes, node, afterId ?? null, asChild);
        return {
          nodes: newNodes,
          definition: syncRows(state.definition, newNodes),
          isDirty: true,
        };
      }),

    updateNode: (id, updates) =>
      set((state) => {
        const newNodes = updateNodeData(state.nodes, id, updates);
        return {
          nodes: newNodes,
          definition: syncRows(state.definition, newNodes),
          isDirty: true,
        };
      }),

    updateNodeRowId: (id, newRowId) =>
      set((state) => {
        const existingIds = collectRowIds(state.nodes);
        const node = flattenTree(state.nodes).find((n) => n.id === id);
        const otherIds = existingIds.filter((rid) => rid !== node?.rowId);
        if (otherIds.includes(newRowId)) return state; // Reject duplicates
        return {
          nodes: updateNodeRowIdUtil(state.nodes, id, newRowId),
          isDirty: true,
        };
      }),

    removeNode: (id) =>
      set((state) => {
        // If deleting a column_headers row, clear all bindings referencing it
        const targetNode = flattenTree(state.nodes).find((n) => n.id === id);
        let newNodes = removeNode(state.nodes, id);
        if (targetNode?.data.type === 'column_headers') {
          newNodes = clearColumnHeadersBindings(newNodes, id);
        }
        return {
          nodes: newNodes,
          definition: syncRows(state.definition, newNodes),
          selectedRowId: state.selectedRowId === id ? null : state.selectedRowId,
          isDirty: true,
        };
      }),

    moveNode: (fromId, toId) =>
      set((state) => {
        const newNodes = moveNodeUtil(state.nodes, fromId, toId);
        return {
          nodes: newNodes,
          definition: syncRows(state.definition, newNodes),
          isDirty: true,
        };
      }),

    indentNode: (id) =>
      set((state) => {
        const newNodes = indentNodeUtil(state.nodes, id);
        return {
          nodes: newNodes,
          definition: syncRows(state.definition, newNodes),
          isDirty: true,
        };
      }),

    outdentNode: (id) =>
      set((state) => {
        const newNodes = outdentNodeUtil(state.nodes, id);
        return {
          nodes: newNodes,
          definition: syncRows(state.definition, newNodes),
          isDirty: true,
        };
      }),

    updateNodeColumnHeadersId: (id, columnHeadersId) =>
      set((state) => ({
        nodes: updateNodeColumnHeadersIdUtil(state.nodes, id, columnHeadersId),
        isDirty: true,
      })),

    getFlatNodes: () => flattenTree(get().nodes),

    // ---- Column actions ----

    addColumn: (column) =>
      set((state) => ({
        definition: {
          ...state.definition,
          columns: [...state.definition.columns, column],
          metadata: { ...state.definition.metadata, updatedAt: new Date() },
        },
        columnConfig: {
          ...state.columnConfig,
          default: [...state.columnConfig.default, column],
        },
        isDirty: true,
      })),

    updateColumn: (id, updates) =>
      set((state) => ({
        definition: {
          ...state.definition,
          columns: state.definition.columns.map((col) =>
            col.id === id ? { ...col, ...updates } : col
          ),
          metadata: { ...state.definition.metadata, updatedAt: new Date() },
        },
        columnConfig: {
          ...state.columnConfig,
          default: state.columnConfig.default.map((col) =>
            col.id === id ? { ...col, ...updates } : col
          ),
        },
        isDirty: true,
      })),

    removeColumn: (id) =>
      set((state) => ({
        definition: {
          ...state.definition,
          columns: state.definition.columns.filter((col) => col.id !== id),
          metadata: { ...state.definition.metadata, updatedAt: new Date() },
        },
        columnConfig: {
          ...state.columnConfig,
          default: state.columnConfig.default.filter((col) => col.id !== id),
        },
        selectedColumnId: state.selectedColumnId === id ? null : state.selectedColumnId,
        isDirty: true,
      })),

    reorderColumns: (fromIndex, toIndex) =>
      set((state) => {
        const columns = [...state.definition.columns];
        const [moved] = columns.splice(fromIndex, 1);
        if (moved) columns.splice(toIndex, 0, moved);
        const defaultCols = [...state.columnConfig.default];
        const [movedDef] = defaultCols.splice(fromIndex, 1);
        if (movedDef) defaultCols.splice(toIndex, 0, movedDef);
        return {
          definition: {
            ...state.definition,
            columns,
            metadata: { ...state.definition.metadata, updatedAt: new Date() },
          },
          columnConfig: { ...state.columnConfig, default: defaultCols },
          isDirty: true,
        };
      }),

    setColumns: (columns) =>
      set((state) => ({
        definition: {
          ...state.definition,
          columns,
          metadata: { ...state.definition.metadata, updatedAt: new Date() },
        },
        columnConfig: { ...state.columnConfig, default: columns },
        selectedColumnId: null,
        isDirty: true,
      })),

    // ---- Level column config ----

    setLevelColumns: (depth, columns) =>
      set((state) => ({
        columnConfig: {
          ...state.columnConfig,
          levels: { ...state.columnConfig.levels, [depth]: columns },
        },
        isDirty: true,
      })),

    setDefaultColumns: (columns) =>
      set((state) => ({
        definition: {
          ...state.definition,
          columns,
          metadata: { ...state.definition.metadata, updatedAt: new Date() },
        },
        columnConfig: { ...state.columnConfig, default: columns },
        isDirty: true,
      })),

    // ---- Per-row column actions (column_headers rows) ----

    addColumnToRow: (nodeId, column) =>
      set((state) => {
        const flat = flattenTree(state.nodes);
        const node = flat.find((n) => n.id === nodeId);
        if (!node || node.data.type !== 'column_headers') return state;
        const newColumns = [...node.data.columns, column];
        const newNodes = updateNodeData(state.nodes, nodeId, { columns: newColumns });
        const newDef = syncRows(state.definition, newNodes);
        return {
          nodes: newNodes,
          definition: { ...newDef, columns: newColumns },
          columnConfig: { ...state.columnConfig, default: newColumns },
          isDirty: true,
        };
      }),

    updateColumnInRow: (nodeId, columnId, updates) =>
      set((state) => {
        const flat = flattenTree(state.nodes);
        const node = flat.find((n) => n.id === nodeId);
        if (!node || node.data.type !== 'column_headers') return state;
        const newColumns = node.data.columns.map((col) =>
          col.id === columnId ? { ...col, ...updates } : col
        );
        const newNodes = updateNodeData(state.nodes, nodeId, { columns: newColumns });
        const newDef = syncRows(state.definition, newNodes);
        return {
          nodes: newNodes,
          definition: { ...newDef, columns: newColumns },
          columnConfig: { ...state.columnConfig, default: newColumns },
          isDirty: true,
        };
      }),

    removeColumnFromRow: (nodeId, columnId) =>
      set((state) => {
        const flat = flattenTree(state.nodes);
        const node = flat.find((n) => n.id === nodeId);
        if (!node || node.data.type !== 'column_headers') return state;
        const newColumns = node.data.columns.filter((col) => col.id !== columnId);
        const newNodes = updateNodeData(state.nodes, nodeId, { columns: newColumns });
        const newDef = syncRows(state.definition, newNodes);
        return {
          nodes: newNodes,
          definition: { ...newDef, columns: newColumns },
          columnConfig: { ...state.columnConfig, default: newColumns },
          isDirty: true,
        };
      }),

    reorderColumnsInRow: (nodeId, fromIndex, toIndex) =>
      set((state) => {
        const flat = flattenTree(state.nodes);
        const node = flat.find((n) => n.id === nodeId);
        if (!node || node.data.type !== 'column_headers') return state;
        const newColumns = [...node.data.columns];
        const [moved] = newColumns.splice(fromIndex, 1);
        if (moved) newColumns.splice(toIndex, 0, moved);
        const newNodes = updateNodeData(state.nodes, nodeId, { columns: newColumns });
        const newDef = syncRows(state.definition, newNodes);
        return {
          nodes: newNodes,
          definition: { ...newDef, columns: newColumns },
          columnConfig: { ...state.columnConfig, default: newColumns },
          isDirty: true,
        };
      }),

    setColumnsForRow: (nodeId, columns) =>
      set((state) => {
        const flat = flattenTree(state.nodes);
        const node = flat.find((n) => n.id === nodeId);
        if (!node || node.data.type !== 'column_headers') return state;
        const newNodes = updateNodeData(state.nodes, nodeId, { columns });
        const newDef = syncRows(state.definition, newNodes);
        return {
          nodes: newNodes,
          definition: { ...newDef, columns },
          columnConfig: { ...state.columnConfig, default: columns },
          isDirty: true,
        };
      }),

    // ---- Layout settings ----

    setLayoutSettings: (updates) =>
      set((state) => ({
        layoutSettings: { ...state.layoutSettings, ...updates },
        isDirty: true,
      })),

    // ---- Column modal ----

    setColumnModalOpen: (open) => set({ columnModalOpen: open }),

    // ---- Selection ----

    selectRow: (id) =>
      set({ selectedRowId: id, selectedColumnId: null }),

    selectColumn: (id) =>
      set({ selectedColumnId: id, selectedRowId: null }),

    // ---- State management ----

    loadTemplate: (template, templateId) =>
      set({
        definition: { ...template },
        nodes: [],
        columnConfig: createDefaultLevelConfig(template.columns),
        layoutSettings: { ...DEFAULT_LAYOUT_SETTINGS },
        selectedRowId: null,
        selectedColumnId: null,
        columnModalOpen: false,
        isDirty: false,
        templateId: templateId ?? null,
        instructionText: null,
      }),

    reset: () => {
      const def = createEmptyDefinition();
      return set({
        definition: def,
        nodes: [],
        columnConfig: createDefaultLevelConfig(def.columns),
        layoutSettings: { ...DEFAULT_LAYOUT_SETTINGS },
        selectedRowId: null,
        selectedColumnId: null,
        columnModalOpen: false,
        isDirty: false,
        templateId: null,
        instructionText: null,
      });
    },
  };
});
